import { useMemo } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';

export interface UtilityAccountLocationData {
  displayServiceAddress?: string;
  stateAbbreviation?: string;
  stateFullName?: string;
  zipCode?: string;
}

interface Props {
  utilityAccountId: number;
}

export const useUtilityAccountLocation = ({ utilityAccountId }: Props) => {
  const {
    loading,
    error,
    data: locationData,
  } = useQuery<
    GetUseUtilityAccountLocationQuery,
    GetUseUtilityAccountLocationQueryVariables
  >(GET_USE_UTILITY_ACCOUNT_LOCATION_QUERY, {
    variables: { id: utilityAccountId },
  });

  const data: UtilityAccountLocationData | undefined = useMemo(() => {
    if (loading || error || !locationData) return undefined;
    const { utilityAccount } = locationData;
    return {
      displayServiceAddress: utilityAccount?.displayServiceAddress,
      stateAbbreviation:
        utilityAccount?.serviceAddressState ||
        utilityAccount?.utility?.state?.abbreviation,
      stateFullName: utilityAccount?.utility?.state?.name,
      zipCode: utilityAccount?.serviceAddressZip || undefined,
    };
  }, [locationData, loading, error]);

  return { data, error, loading };
};

export const GET_USE_UTILITY_ACCOUNT_LOCATION_QUERY = gql`
  query GetUseUtilityAccountLocation($id: Int!) {
    utilityAccount(id: $id) {
      id
      displayServiceAddress
      serviceAddressState
      serviceAddressZip
      utility {
        id
        state {
          id
          name
          abbreviation
        }
      }
    }
  }
`;

export const getUtilityAccountLocationMock = {
  utilityAccount: {
    id: 100,
    displayServiceAddress: '123 Fake St, Falseville, FK 99999',
    serviceAddressState: 'FK',
    serviceAddressZip: '99999',
    utility: {
      id: 2,
      state: {
        abbreviation: 'ZZ',
        id: 50,
        name: 'Zarizona',
      },
    },
  },
};

export const useUtilityAccountLocationMock = {
  data: {
    displayServiceAddress: '123 Fake St, Falseville, FK 99999',
    stateAbbreviation: 'FK',
    stateFullName: 'Zarizona',
    zipCode: '99999',
  },
  loading: false,
  error: undefined,
};
